import { Link, useDictionary } from "gemi/client";
import { useEffect, useState } from "react";
import { cookieConsentDict } from "../components/CookieConsentBanner.i18n";
import {
  type Consent,
  CONSENT_REOPEN_EVENT,
  loadMetaPixel,
  readConsent,
  writeConsent,
} from "../utils/consentAnalytics";
import { clearMetaClickIds, syncMetaClickIds } from "../utils/metaClickIds";
import { CARD_SHADOW, font } from "./_shared";

// Cookie-consent banner for the /v2 marketing site. It shares the consent cookie + Pixel-loading logic
// (consentAnalytics) and the CookieConsent dictionary with the rest of the app, but wears the /v2
// design language (soft card, warm-black pill CTA). Consent gates the Meta Pixel only (GA is
// first-party and already loads app-wide from RootLayout); this collects the decision and, on accept,
// loads the Pixel — the funnel ConsentedMetaPixel covers a returning visitor.
export function CookieConsentBannerV2({ pixelId }: { pixelId: string | null }) {
  const t = useDictionary(cookieConsentDict);
  // SSR-safe: never read the cookie in the initializer. "unknown" until the mount effect resolves it,
  // so server and first client render agree (no banner) and there is no hydration flash.
  const [consent, setConsent] = useState<Consent | "unknown" | null>("unknown");

  useEffect(() => {
    setConsent(readConsent());
  }, []);

  // Reopen when the footer "Cookie preferences" control fires, so a decided visitor can change or
  // withdraw consent. Registered unconditionally (before the early returns) so it is always listening.
  useEffect(() => {
    const reopen = () => setConsent(null);
    window.addEventListener(CONSENT_REOPEN_EVENT, reopen);
    return () => window.removeEventListener(CONSENT_REOPEN_EVENT, reopen);
  }, []);

  // Nothing to consent to until the Meta Pixel is configured: GA is first-party and loads regardless,
  // so with no pixel id the banner would gate nothing. Keeps the feature fully inert without creds.
  if (!pixelId) return null;
  if (consent !== null) return null;

  const accept = () => {
    writeConsent("granted");
    if (import.meta.env.PROD) {
      // Now that consent covers it, materialize `_fbc` from the `fbclid` still in the landing URL.
      syncMetaClickIds(pixelId);
      loadMetaPixel(pixelId);
    }
    setConsent("granted");
  };
  const decline = () => {
    writeConsent("denied");
    // Drop any click-id cookies so a declining (or withdrawing) visitor keeps none.
    if (import.meta.env.PROD) clearMetaClickIds();
    setConsent("denied");
  };

  return (
    <div
      className="fixed inset-x-4 bottom-4 z-50 sm:bottom-6"
      style={font.body}
    >
      <div
        className="mx-auto flex max-w-[640px] flex-col gap-4 rounded-2xl border border-[rgba(25,24,24,0.1)] bg-white/95 p-5 backdrop-blur sm:flex-row sm:items-center sm:justify-between"
        style={{ boxShadow: CARD_SHADOW }}
      >
        <p className="m-0 text-[14px] leading-[1.4] text-[#343139]">
          {t("message")}{" "}
          <Link
            href="/privacy-policy"
            className="font-medium text-[#343139] underline underline-offset-2"
          >
            {t("privacy_link")}
          </Link>
        </p>
        <div className="flex shrink-0 items-center gap-3">
          <button
            type="button"
            onClick={decline}
            className="rounded-full border border-[rgba(25,24,24,0.16)] bg-white px-5 py-[10px] text-[14px] font-medium text-[#343139]"
          >
            {t("reject")}
          </button>
          <button
            type="button"
            onClick={accept}
            className="rounded-full bg-[#19180F] px-5 py-[10px] text-[14px] font-medium text-[#F7F7F2]"
          >
            {t("accept")}
          </button>
        </div>
      </div>
    </div>
  );
}
